import { useMutation, useQuery } from '@tanstack/react-query'
import { InputNumber, Space } from 'antd'
import registrationApi from 'api/registrationApi'
import { setAppSpinning } from 'app/appSlice'
import AppDnD from 'general/components/AppDragAndDrop/AppDnD'
import CustomAntButton from 'general/components/Button/CustomAntButton'
import ContentContainer from 'general/components/ContentContainer'
import CustomDataTable from 'general/components/CustomDataTable'
import CustomAntInput from 'general/components/Forms/CustomAntInput/CustomAntInput'
import KTTooltip from 'general/components/eInvoiceComponents/KTTooltip'
import ModalUnauthorized from 'general/components/Modal/ModalUnauthorized'
import ModalDataTransmissionLog from 'general/components/ModalDataTransmissionLog'
import PreferenceKeys from 'general/constants/PreferenceKeys'
import ToastHelper from 'general/helpers/ToastHelper'
import Utils, { padNumber } from 'general/utils/Utils'
import useAppDnD from 'hooks/useAppDnD'
import useFilter from 'hooks/useFilter'
import { useAppDispatch } from 'hooks/useRedux'
import useRouter from 'hooks/useRouter'
import useSearchDebounce from 'hooks/useSearchDebounce'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { listRegistrationQueries } from './queries'
import './style.scss'
import { getColumns } from './columns'
import ModalWarning from 'general/components/Modal/ModalWarning'
import dayjs from 'dayjs'
import companyApi from 'api/companyApi'
import { thunkGetCompanyProfile } from 'features/System/systemSlice'
import { useDispatch } from 'react-redux'

ListRegistration.propTypes = {}

function ListRegistration() {
  // MARK --- Parmas: ---
  const { navigate } = useRouter()

  const { t } = useTranslation()
  const appDispatch = useAppDispatch()
  const userFunction = useSelector((state) => state?.function?.userFunctions)
  const listFunction = useSelector((state) => state?.function?.listFunctions)
  const [filters, setFilters] = useFilter(
    'registrationList',
    'gFiltersRegistrationList',
  )
  const [isCreateRegistration, isEditRegistration, isDeleteRegistration] =
    useMemo(() => {
      return Utils.checkFunction(
        [
          {
            listFunctionCode: 'TOKHAI_ADD',
          },
          {
            listFunctionCode: 'TOKHAI_EDIT',
          },
          {
            listFunctionCode: 'TOKHAI_DELETE',
          },
        ],
        userFunction,
        listFunction,
      )
    }, [userFunction, listFunction])
  const { debouncedSearch, search, setSearch } = useSearchDebounce('')
  const [activeRegistration, setActiveRegistration] = useState(null)

  const [modalState, setModalState] = useState({
    noRight: false,
    dataTransmissionLog: false,
    delete: false,
    updateSTT: {
      state: false,
      data: null,
    },
  })
  const dispatch = useDispatch()
  const { company } = useSelector((state) => state.system)
  const [companyCount, setCompanyCount] = useState(company.count || 1)

  const toggleModal = (key, value) => {
    setModalState((prev) => ({ ...prev, [key]: value }))
  }

  async function actionCreateRegistration() {
    if (isCreateRegistration) navigate('tao')
    if (!isCreateRegistration) toggleModal('noRight', true)
  }

  const {
    data: registrationData,
    isFetching: gettingRegistrationData,
    refetch: refetchRegistrationData,
  } = useQuery(listRegistrationQueries.listRegistration(filters))

  const { mutate: deleteRegistration } = useMutation({
    mutationKey: ['deleteRegistration'],
    mutationFn: (registrationId) =>
      registrationApi.deleteRegistration(registrationId),
    onMutate: () => appDispatch(setAppSpinning(true)),
    onSettled: () => appDispatch(setAppSpinning(false)),
    onSuccess: () => {
      ToastHelper.showSuccess('Xóa tờ khai thành công')
      refetchRegistrationData()
    },
  })

  const { mutate: viewPdfRegistration } = useMutation({
    mutationKey: ['exportRegistration'],
    mutationFn: (registrationId) =>
      registrationApi.exportRegistrationById(registrationId),
    onMutate: () => appDispatch(setAppSpinning(true)),
    onSettled: () => appDispatch(setAppSpinning(false)),
    onSuccess: (res) => {
      const blobData = new File(
        [res],
        'Tờ khai đăng ký/thay đổi thông tin sử dụng hóa đơn.pdf',
        { type: 'application/pdf' },
      )
      window.open(URL.createObjectURL(blobData), '_blank')
    },
  })

  const { mutate: syncRegistration } = useMutation({
    mutationKey: ['syncRegistration'],
    mutationFn: (registrationId) =>
      registrationApi.syncRegistration(registrationId),
    onMutate: () => appDispatch(setAppSpinning(true)),
    onSettled: () => appDispatch(setAppSpinning(false)),
    onSuccess: () => {
      ToastHelper.showSuccess('Đồng bộ tờ khai thành công')
      refetchRegistrationData()
    },
  })

  const { mutate: updateCompany } = useMutation({
    mutationKey: ['updateCompany'],
    mutationFn: () => companyApi.update({ count: companyCount }),
    onMutate: () => appDispatch(setAppSpinning(true)),
    onSettled: () => appDispatch(setAppSpinning(false)),
    onSuccess: () => {
      ToastHelper.showSuccess('Cập nhật số thứ tự thành công')
      dispatch(thunkGetCompanyProfile({ companyId: company.companyId }))
    },
  })

  const fullColumns = useMemo(
    () =>
      getColumns({
        deleteRegistration,
        isDeleteRegistration,
        isEditRegistration,
        navigate,
        syncRegistration,
        viewPdfRegistration,
        setActiveRegistration,
        toggleModal,
      }),
    [
      deleteRegistration,
      isDeleteRegistration,
      isEditRegistration,
      navigate,
      syncRegistration,
      viewPdfRegistration,
    ],
  )

  const dynamicColumns = useAppDnD(fullColumns)

  useEffect(() => {
    setFilters({
      q: debouncedSearch,
      page: 1,
    })
  }, [debouncedSearch, setFilters])

  const codeTemplate = useMemo(() => {
    let x = 'x'
    if (modalState.updateSTT?.data?.usingOther) x = 5
    if (modalState.updateSTT?.data?.usingSale) x = 2
    if (modalState.updateSTT?.data?.usingVat) x = 1

    return `M${x}-${dayjs().year() % 100}-${company?.issuedTaxAuthorityCode || '...'}-${padNumber(companyCount || company?.count, 11)}`
  }, [modalState.updateSTT, companyCount])

  useEffect(() => {
    if (modalState.updateSTT.state) {
      setCompanyCount(company.count || 1)
    }
  }, [modalState.updateSTT])
  return (
    <ContentContainer wrapperClassName={'ListRegistration'}>
      <ContentContainer.Header
        titleContent={'Danh sách tờ khai'}
        description={`Tổng: ${registrationData?.total} tờ khai`}
        toolBar={
          <Space>
            <KTTooltip text="Nhập số tờ khai để tìm">
              <span style={{ display: 'inline-flex' }}>
                <CustomAntInput
                  borderRadius="sm"
                  type="search"
                  inputProps={{
                    name: 'search',
                    defaultValue: search,
                    placeholder: 'Nhập số tờ khai để tìm',
                    onChange: (e) => setSearch(e.target.value),
                  }}
                />
              </span>
            </KTTooltip>
            <CustomAntButton
              text={t('AddNew')}
              iconCn="fa-regular fa-plus"
              antProps={{
                type: 'primary',
                onClick: () => actionCreateRegistration(),
              }}
            />
            <AppDnD
              defaultColumns={fullColumns}
              localStorageName={PreferenceKeys.savedColumnsRegistration}
            />
          </Space>
        }
      />
      <ContentContainer.Body>
        <CustomDataTable
          columns={dynamicColumns}
          dataSource={registrationData?.registrations}
          isLoading={gettingRegistrationData}
          isClickable
          handleDoubleClickRow={(row) =>
            viewPdfRegistration(row.registrationId)
          }
          pagination={{
            rowsPerPage: filters?.limit,
            total: registrationData?.total,
            currentPage: filters?.page,
            onChangePage: (page) =>
              setFilters({
                page,
              }),
            onChangeRowsPerPage: (page) => {
              setFilters({
                limit: page,
                page: 1,
              })
            },
          }}
        />
      </ContentContainer.Body>
      <ModalWarning
        show={modalState.delete}
        setShow={(state) => toggleModal('delete', state)}
        header="Xác nhận"
        content="Bạn có thực sự muốn xóa tờ khai đơn đã chọn không?"
        handleConfirm={() => {
          deleteRegistration(activeRegistration.registrationId)
        }}
      />
      <ModalWarning
        iconTitle={<></>}
        show={modalState.updateSTT.state}
        setShow={(state) =>
          toggleModal('updateSTT', { ...modalState.updateSTT, state })
        }
        header="Cập nhật số thứ tự bắt đâu mã CQT"
        content={
          <div>
            <p style={{ marginBottom: '16px' }}>
              Việc nhập sai số thứ tự bắt đầu có thể dẫn đến sai sót trong việc
              <br /> phát hành hóa đơn.
            </p>
            <div
              style={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px',
              }}
            >
              Số thứ tự bắt đầu
              <span className="text-danger">*</span>
            </div>

            <InputNumber
              min={1}
              maxLength={11}
              value={companyCount}
              onChange={(v) => {
                setCompanyCount(v)
              }}
              onKeyDown={(event) => {
                const allowedKeys = [
                  'Backspace',
                  'ArrowLeft',
                  'ArrowRight',
                  'Delete',
                  'Tab',
                ]

                if (!/\d/.test(event.key) && !allowedKeys.includes(event.key)) {
                  event.preventDefault()
                }
              }}
              style={{ width: '80px', padding: '2px 0', marginTop: '4px' }}
            />

            <div
              style={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px',
                marginTop: '10px',
              }}
            >
              Mẫu hiển thị
            </div>

            <input
              type="text"
              className="form-control"
              style={{
                flex: 'auto',
                minWidth: '250px',
                marginTop: '4px',
                height: '36px',
              }}
              value={codeTemplate}
              disabled={true}
            />
          </div>
        }
        handleConfirm={updateCompany}
      />
      <ModalDataTransmissionLog
        show={modalState.dataTransmissionLog}
        onHide={() => toggleModal('dataTransmissionLog', false)}
        viewPdfRegistration={viewPdfRegistration}
        registration={activeRegistration}
      />
      <ModalUnauthorized
        show={modalState.noRight}
        setShow={(state) => toggleModal('noRight', state)}
      />
    </ContentContainer>
  )
}

export default ListRegistration
